import { useNavigate } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { ShieldCheck, Check, Sparkles, Zap, Shield, Globe } from 'lucide-react'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useTheme } from '../../contexts/ThemeContext'
import { useState, useEffect } from 'react'
import { useIsMobile } from '../../hooks/useMediaQuery'
import { useTranslation } from 'react-i18next'
import './PricingPage.css'
import './PricingPage-mobile-fix.css'

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0)
  
  useEffect(() => {
    let startTime: number
    let animationFrameId: number
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)
      
      setCount(Math.floor(progress * value))
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate)
      }
    }
    
    animationFrameId = requestAnimationFrame(animate)
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [value, duration])
  
  return <>{count}</>
}

const PricingPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { scrollYProgress } = useScroll()
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const [expandedCard, setExpandedCard] = useState<string | null>(null)
  
  // Parallax transforms
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, -50])
  const cardsY = useTransform(scrollYProgress, [0, 0.5], [0, -30])

  // Billing toggle state
  const [isYearly, setIsYearly] = useState(true)
  
  // Pricing data
  const pricingPlans = [
    {
      id: 'starter',
      name: t('pricing.plans.starter.name', 'Starter'),
      description: t('pricing.plans.starter.description', 'Perfect for small companies'),
      employees: t('pricing.plans.starter.employees', 'Up to 50 employees'),
      monthlyPrice: 49,
      yearlyPrice: 39,
      features: [
        t('pricing.features.anonymous', 'Anonymous reporting portal'),
        t('pricing.features.messaging', 'Secure messaging system'),
        t('pricing.features.caseManagement', 'Case management'),
        t('pricing.features.emailNotifications', 'Email notifications'),
        t('pricing.features.languageSupport1', '1 language support'),
        t('pricing.features.standardSupport', 'Standard support (48h)'),
        t('pricing.features.euCompliant', 'EU compliant')
      ],
      highlighted: [] as string[]
    },
    {
      id: 'professional',
      name: t('pricing.plans.professional.name', 'Professional'),
      description: t('pricing.plans.professional.description', 'For growing organizations'),
      employees: t('pricing.plans.professional.employees', '51-500 employees'),
      monthlyPrice: 149,
      yearlyPrice: 119,
      features: [
        t('pricing.features.everythingInStarter', 'Everything in Starter, plus:'),
        t('pricing.features.advancedCase', 'Advanced case management'),
        t('pricing.features.customBranding', 'Custom branding'),
        t('pricing.features.languageSupport7', '7 language support'),
        t('pricing.features.prioritySupport', 'Priority support (24h)'),
        t('pricing.features.analytics', 'Analytics dashboard'),
        t('pricing.features.departments', 'Multiple departments'),
        t('pricing.features.apiAccess', 'API access')
      ],
      highlighted: [t('pricing.features.analytics', 'Analytics dashboard'), t('pricing.features.languageSupport7', '7 languages')],
      recommended: true
    },
    {
      id: 'enterprise',
      name: t('pricing.plans.enterprise.name', 'Enterprise'),
      description: t('pricing.plans.enterprise.description', 'For large corporations'),
      employees: t('pricing.plans.enterprise.employees', '500+ employees'),
      monthlyPrice: null,
      yearlyPrice: null,
      features: [
        t('pricing.features.everythingInPro', 'Everything in Professional, plus:'),
        t('pricing.features.unlimited', 'Unlimited employees'),
        t('pricing.features.allLanguages', 'All EU languages'),
        t('pricing.features.dedicatedSupport', 'Dedicated support'),
        t('pricing.features.customIntegrations', 'Custom integrations'),
        t('pricing.features.onPremise', 'On-premise option'),
        t('pricing.features.advancedSecurity', 'Advanced security'),
        t('pricing.features.training', 'Training included')
      ],
      highlighted: [t('pricing.features.dedicatedSupport', 'Dedicated support'), t('pricing.features.onPremise', 'On-premise option')]
    }
  ]

  const faqs = [
    {
      question: t('pricing.faq.compliance.question', 'Is WhistlePro compliant with EU Directive 2019/1937?'),
      answer: t('pricing.faq.compliance.answer', 'Yes, WhistlePro is fully compliant with the EU Whistleblowing Directive. We provide all required features including anonymous reporting, secure communication channels, and proper data retention policies.')
    },
    {
      question: t('pricing.faq.setup.question', 'How long does setup take?'),
      answer: t('pricing.faq.setup.answer', 'Most organizations are up and running in under 30 minutes. Our guided setup process walks you through everything step-by-step.')
    },
    {
      question: t('pricing.faq.trial.question', 'Do I need a credit card for the trial?'),
      answer: t('pricing.faq.trial.answer', 'No credit card required. Start your 14-day free trial instantly and only add payment details if you decide to continue.')
    },
    {
      question: t('pricing.faq.changePlans.question', 'Can I change plans later?'),
      answer: t('pricing.faq.changePlans.answer', 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at your next billing cycle.')
    }
  ]

  return (
    <div className={`pricing-page-minimal ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Enhanced Hero Section with Parallax */}
      <section className="pricing-hero-minimal">
        {/* Animated Background Gradient */}
        <div className="hero-gradient-bg">
          <div className="gradient-orb gradient-orb-1" />
          <div className="gradient-orb gradient-orb-2" />
          <div className="gradient-orb gradient-orb-3" />
        </div>
        
        <div className="container mx-auto px-4">
          <motion.div
            style={{ y: heroY }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto relative z-10"
          >
            <motion.div 
              className="pricing-label"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <Sparkles className="label-icon" />
              {t('nav.pricing', 'Pricing')}
            </motion.div>
            <motion.h1 
              className="pricing-hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t('pricing.hero.title', 'Simple, Transparent Pricing')}
            </motion.h1>
            <motion.p 
              className="pricing-hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('pricing.hero.subtitle', 'Choose the plan that fits your company size. All plans include full EU compliance features.')}
            </motion.p>
            
            {/* Trust Indicators */}
            <motion.div 
              className="trust-indicators"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="trust-item">
                <Shield className="trust-icon" />
                <span>{t('pricing.trust.gdpr', 'GDPR Compliant')}</span>
              </div>
              <div className="trust-item">
                <Globe className="trust-icon" />
                <span>{t('pricing.trust.euServers', 'EU Servers')}</span>
              </div>
              <div className="trust-item">
                <Zap className="trust-icon" />
                <span>{t('pricing.trust.uptime', '99.9% Uptime')}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="pricing-cards-section">
        <motion.div 
          className="container mx-auto px-4"
          style={{ y: cardsY }}
        >
          {/* Billing Toggle */}
          <motion.div 
            className="billing-toggle-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="billing-toggle">
              <span className={`toggle-label ${!isYearly ? 'active' : ''}`}>{t('pricing.billing.monthly', 'Monthly')}</span>
              <button
                className="toggle-switch"
                onClick={() => setIsYearly(!isYearly)}
                aria-label="Toggle billing period"
              >
                <motion.div 
                  className="toggle-thumb"
                  animate={{ x: isYearly ? 24 : 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              </button>
              <span className={`toggle-label ${isYearly ? 'active' : ''}`}>
                {t('pricing.billing.yearly', 'Yearly')}
                <Badge className="save-badge">{t('pricing.billing.save', 'Save 20%')}</Badge>
              </span>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <div className="pricing-cards-grid-new">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                className={`pricing-card-new ${plan.recommended ? 'recommended' : ''} ${hoveredCard === plan.id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredCard(plan.id)}
                onMouseLeave={() => setHoveredCard(null)}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.3 }
                }}
              >
                {plan.recommended && (
                  <div className="recommended-badge-new">
                    <Sparkles className="badge-icon" />
                    {t('pricing.badge.popular', 'MOST POPULAR')}
                  </div>
                )}
                
                {/* Card Header */}
                <div className="card-header-new">
                  <h3 className="card-name">{plan.name}</h3>
                  <p className="card-description">{plan.description}</p>
                  <p className="card-employees">{plan.employees}</p>
                </div>
                
                {/* Pricing */}
                {plan.monthlyPrice ? (
                  <div className="pricing-section-new">
                    <div className="price-container">
                      <span className="currency">€</span>
                      <motion.span 
                        className="price"
                        key={isYearly ? 'yearly' : 'monthly'}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {hoveredCard === plan.id ? (
                          <AnimatedCounter value={isYearly ? plan.yearlyPrice! : plan.monthlyPrice} />
                        ) : (
                          isYearly ? plan.yearlyPrice : plan.monthlyPrice
                        )}
                      </motion.span>
                      <span className="period">/{t('pricing.period.month', 'month')}</span>
                    </div>
                    {isYearly && (
                      <motion.p 
                        className="yearly-billing"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {t('pricing.billing.annualTotal', '€{{amount}} billed annually', { amount: plan.yearlyPrice! * 12 })}
                      </motion.p>
                    )}
                  </div>
                ) : (
                  <div className="custom-pricing-section">
                    <p className="custom-pricing-text">{t('pricing.customPricing', 'Custom Pricing')}</p>
                    <p className="custom-pricing-subtitle">{t('pricing.contactQuote', 'Contact us for a quote')}</p>
                  </div>
                )}
                
                {/* CTA Button */}
                <div className="cta-section">
                  {plan.monthlyPrice ? (
                    <Button 
                      className={`pricing-cta-btn ${plan.recommended ? 'primary' : 'secondary'}`}
                      onClick={() => navigate(`/register?plan=${plan.id}&billing=${isYearly ? 'yearly' : 'monthly'}`)}
                    >
                      {plan.recommended ? t('pricing.buttons.getStarted', 'Get Started') : t('pricing.buttons.freeTrial', 'Start Free Trial')}
                    </Button>
                  ) : (
                    <Button 
                      className="pricing-cta-btn enterprise"
                      onClick={() => window.location.href = `mailto:sales@whistlepro.eu?subject=${encodeURIComponent(t('pricing.email.subject', 'Enterprise Plan Inquiry'))}`}
                    >
                      {t('pricing.buttons.contactSales', 'Contact Sales')}
                    </Button>
                  )}
                </div>
                
                {/* Features List */}
                <div className="features-list">
                  {/* On mobile, show first 3 features, rest collapsible */}
                  {(isMobile && expandedCard !== plan.id ? plan.features.slice(0, 3) : plan.features).map((feature, featureIndex) => (
                    <motion.div 
                      key={featureIndex}
                      className={`feature-item-new ${plan.highlighted.includes(feature) ? 'highlighted' : ''}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * featureIndex + 0.3 }}
                    >
                      {feature.includes('Everything in') || feature === 'Contact Sales' ? (
                        <span className="feature-text inherit">{feature}</span>
                      ) : (
                        <>
                          <Check className="feature-check-icon" />
                          <span className="feature-text">{feature}</span>
                        </>
                      )}
                    </motion.div>
                  ))}
                  {/* Show more/less button on mobile */}
                  {isMobile && plan.features.length > 3 && (
                    <button
                      className="show-more-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCard(expandedCard === plan.id ? null : plan.id);
                      }}
                    >
                      {expandedCard === plan.id ? t('pricing.showLess', 'Show less') : t('pricing.showMore', 'Show {{count}} more features', { count: plan.features.length - 3 })}
                      <motion.span
                        animate={{ rotate: expandedCard === plan.id ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ display: 'inline-block' }}
                      >
                        ↓
                      </motion.span>
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="features-note"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <ShieldCheck className="note-icon" />
            <span>{t('pricing.allPlansInclude', 'All plans include: Anonymous reporting • Two-way messaging • Case management • Compliance reporting • Multi-language support • GDPR compliant')}</span>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="features-title">{t('pricing.features.title', 'Everything you need for EU compliance')}</h2>
            <div className="features-grid">
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.anonymous', 'Anonymous reporting portal')}</span>
              </div>
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.messaging', 'Secure two-way messaging')}</span>
              </div>
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.caseManagement', 'Case management dashboard')}</span>
              </div>
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.audit', 'Audit trail for compliance')}</span>
              </div>
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.multiLanguage', 'Multi-language support (7 EU languages)')}</span>
              </div>
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.gdpr', 'GDPR compliant data handling')}</span>
              </div>
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.customBranding', 'Custom branding options')}</span>
              </div>
              <div className="feature-item">
                <Check className="feature-check" />
                <span>{t('pricing.features.uptime', '99.9% uptime SLA')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="faq-title">
              <QuestionCircleOutlined className="mr-2" />
              {t('pricing.faq.title', 'Frequently Asked Questions')}
            </h2>
          </motion.div>

          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="faq-item"
              >
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bottom-cta">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-title">{t('pricing.cta.title', 'Ready to ensure compliance?')}</h2>
            <p className="cta-subtitle">
              {t('pricing.cta.subtitle', 'Join hundreds of European companies using WhistlePro')}
            </p>
            <Button
              className="cta-button-main"
              onClick={() => navigate('/signup')}
            >
              {t('pricing.cta.button', 'Start Free 14-Day Trial')}
            </Button>
            <p className="cta-note">
              {t('pricing.cta.note', 'No credit card required • Setup in 30 minutes')}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default PricingPage